import { Injectable } from '@angular/core';
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  FirebaseStorage,
} from 'firebase/storage';
import { Observable, from, firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { HttpModel } from '../../models/http-details';
import { HttpService } from './http.service';
import {
  getAuth,
  signInWithCustomToken,
  Auth,
} from 'firebase/auth';

export interface FirebaseTokenRequest {
  resourceType: string;
  resourceId: string;
}

export interface FirebaseTokenResponse {
  firebase_token: string;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseStorageService {
  private app!: FirebaseApp;
  private defaultStorage!: FirebaseStorage; // For storageBucket
  private authStorage!: FirebaseStorage;    // For storageAuthBucket
  private isInitialized = false;
  private auth: Auth;

  constructor(private httpService: HttpService) {
    this.initializeFirebase();
    this.auth = getAuth(this.app);
  }

  private initializeFirebase(): void {
    try {
      this.app = initializeApp(environment.firebaseConfig);
      this.defaultStorage = getStorage(this.app, environment.firebaseConfig.storageBucket); // Initialize with default bucket
      this.authStorage = getStorage(this.app, environment.firebaseConfig.storageAuthBucket); // Initialize with auth bucket
      this.isInitialized = true;
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      this.isInitialized = false;
    }
  }

  /**
   * Internal helper to get the correct FirebaseStorage instance.
   * @param useAuthBucket - If true, uses storageAuthBucket; otherwise, uses storageBucket.
   */
  private getStorageInstance(useAuthBucket: boolean): FirebaseStorage {
    if (!this.isInitialized) {
      throw new Error('Firebase not properly initialized');
    }
    return useAuthBucket ? this.authStorage : this.defaultStorage;
  }

  /**
   * Upload file to Firebase Storage using the default storageBucket.
   * @param file - File to upload
   * @param path - Storage path for the file
   * @returns Observable with download URL
   */
  uploadFile(file: File, path: string): Observable<string> {
    return from(this.uploadFileInternal(file, path, false)); // Use default bucket with default validation (5MB, PDF/PNG/DOC)
  }

  private async uploadFileInternal(file: File, path: string, useAuthBucket: boolean, allowedTypes?: string[], maxSizeMB?: number): Promise<string> {
    try {
      const storageInstance = this.getStorageInstance(useAuthBucket);
      if (!file) {
        throw new Error('No file provided for upload');
      }

      // Default validation for signup component (5MB, PDF/PNG/DOC only)
      const defaultMaxSize = 5 * 1024 * 1024; // 5MB
      const defaultAllowedTypes = [
        'application/pdf',
        'image/png',
        'application/msword',
      ];

      // Use provided validation rules or defaults
      const maxSize = maxSizeMB ? maxSizeMB * 1024 * 1024 : defaultMaxSize;
      const validAllowedTypes = allowedTypes || defaultAllowedTypes;

      // Validate file size
      if (file.size > maxSize) {
        const sizeLimitMB = maxSize / (1024 * 1024);
        throw new Error(`File size exceeds ${sizeLimitMB}MB limit`);
      }

      // Validate file type
      if (!validAllowedTypes.includes(file.type)) {
        throw new Error('File type not supported. Please check the allowed file formats.');
      }

      const storageRef = ref(storageInstance, path); // Use the selected storage instance
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      return downloadUrl;
      return downloadUrl;
    } catch (error: any) {
      console.error(' File upload failed:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }
  }

  /**
   * Delete file from Firebase Storage using the default storageBucket.
   * @param path - Storage path of the file to delete
   * @returns Observable<void>
   */
  deleteFile(path: string): Observable<void> {
    return from(this.deleteFileInternal(path, false)); // Use default bucket
  }

  /**
   * Delete file from Firebase Storage using the auth storage bucket.
   * Use when file was uploaded via uploadAuthFile (rca-assessment uploads).
   * @param path - Storage path of the file to delete
   * @returns Observable<void>
   */
  deleteAuthFile(path: string): Observable<void> {
    return from(this.deleteFileInternal(path, true));
  }

  private async deleteFileInternal(path: string, useAuthBucket: boolean): Promise<void> {
    try {
      const storageInstance = this.getStorageInstance(useAuthBucket);
      const storageRef = ref(storageInstance, path); // Use the selected storage instance
      await deleteObject(storageRef);
    } catch (error: any) {
      console.error(' File deletion failed:', error);
      throw new Error(`Delete failed: ${error.message}`);
    }
  }

  /**
   * Generate a unique file path for uploads
   * @param userId - User identifier (email or temp ID)
   * @param fileName - Original file name
   * @param category - File category (e.g., 'certificates')
   * @returns Generated file path
   */
  generateFilePath(userId: string, fileName: string, category: string = 'certificates'): string {
    const timestamp = new Date()?.getTime();
    const sanitizedUserId = userId?.replace(/[^a-zA-Z0-9]/g, '_');
    const sanitizedFileName = fileName?.replace(/[^a-zA-Z0-9.-]/g, '_');
    return `${category}/${sanitizedUserId}/${timestamp}_${sanitizedFileName}`;
  }

  /**
   * Download file by creating a temporary link
   * @param url - Download URL
   * @param fileName - Name for the downloaded file
   */
  downloadFile(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private getFirebaseToken(): Promise<FirebaseTokenResponse> {
    const httpObj = new HttpModel(
      '/auth/firebase-token',
      {},
      null,
      "GET"
    );

    return firstValueFrom(
      this.httpService.invokeHttp(httpObj)
    ).then((response: any) => {
      return response.body || response;
    }).catch(error => {
      if (error.status === 404) {
        throw new Error('Firebase authentication endpoint not found. Please ensure the backend /auth/firebase-token endpoint is implemented.');
      } else if (error.status === 401 || error.status === 403) {
        throw new Error('Not authorized. Please ensure you are logged in and try again.');
      } else {
        throw new Error(`Unable to authenticate with Firebase: ${error.message || 'Unknown error'}`);
      }
    });
  }

  public async uploadAuthFile(file: File, resourceType: string, resourceId: string): Promise<string> {
    try {
      // Get Firebase token for the specific resource
      const tokenResponse = await this.getFirebaseToken();
      const firebaseToken = tokenResponse.firebase_token;

      if (!firebaseToken) {
        throw new Error('No firebase_token received from backend');
      }

      // Authenticate with Firebase using the custom token
      await signInWithCustomToken(this.auth, firebaseToken);

      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('Firebase authentication failed - no user found after sign-in.');
      }

      // Wait for token to be processed
      await new Promise(resolve => setTimeout(resolve, 100));

      // Construct the file path
      const timestamp = new Date().getTime();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${resourceType}/${resourceId}/${timestamp}_${sanitizedFileName}`;

      // Define validation rules based on resource type
      let allowedTypes: string[];
      let maxSizeMB: number;

      if (resourceType === 'rca-assessment') {
        // RCA Assessment: Support more file types and larger size
        allowedTypes = [
          'application/pdf',
          'application/msword', // .doc
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
          'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
          'image/png',
          'image/jpeg',
          'image/jpg',
        ];
        maxSizeMB = 5; // 5MB for RCA assessments
      } else {
        // Default: Use signup validation (PDF, PNG, DOC only)
        allowedTypes = [
          'application/pdf',
          'image/png',
          'application/msword',
        ];
        maxSizeMB = 5; // 5MB default
      }

      // Use the internal upload method with useAuthBucket = true and specific validation
      const downloadUrl = await this.uploadFileInternal(file, filePath, true, allowedTypes, maxSizeMB);

      return downloadUrl;

    } catch (error) {
      if (error instanceof Error) {
        const errorCode = (error as any).code;

        if (errorCode === 'storage/unauthorized') {
          throw new Error(`Not authorized to upload files. Firebase Storage access denied for ${resourceType}/${resourceId}. Please ensure Firebase Storage Rules are correctly configured.`);
        } else if (errorCode === 'storage/canceled') {
          throw new Error('Upload was canceled. Please try again.');
        } else {
          throw new Error(`Upload failed: ${error.message}`);
        }
      } else {
        throw new Error('Upload failed due to an unknown error. Please try again.');
      }
    }
  }

  // Convenience method for rca-assessment uploads
  public async uploadSoftwarePackage(file: File, softwareId: string): Promise<string> {
    return this.uploadAuthFile(file, 'rca-assessment', softwareId);
  }
}